import React, { createContext, useContext, useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../apiBase';
import { ACCENTS, ACCENT_PRESETS } from '../lib/themeAccents';
import { applyAccentPreset, applyBrandPrimaryFromHex, applyAppearanceColors } from '../lib/brandPrimary';
export { ACCENTS, ACCENT_PRESETS } from '../lib/themeAccents';

const ThemeContext = createContext(null);

export const THEME_STORAGE_KEY = 'aurax-theme';
export const PUBLIC_THEME_STORAGE_KEY = 'aurax-public-theme-v1';
export const ACCENT_STORAGE_KEY = 'aurax-accent';
/** Preferência pessoal do header — não é sobrescrita por brand_accent vazio do servidor. */
export const ACCENT_USER_KEY = 'aurax-accent-user';
/** Preferência pessoal claro/escuro — não é sobrescrita por brand_theme do escritório. */
export const THEME_USER_KEY = 'aurax-theme-user';
const STORAGE_KEY = THEME_STORAGE_KEY;
const ACCENT_KEY = ACCENT_STORAGE_KEY;
const VALID_THEMES = ['light', 'dark'];

const VALID_ACCENTS = ACCENTS.map((p) => p.key);

const applyThemeDom = (resolved, { persistAliases = true } = {}) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
  root.style.backgroundColor = resolved === 'dark' ? '#0f1014' : '#FAFAFA';
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  themeMeta?.setAttribute('content', resolved === 'dark' ? '#0f1014' : '#FAFAFA');
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  try {
    if (!persistAliases) return;
    window.localStorage.setItem('theme_mode', resolved);
    window.localStorage.setItem('us-theme', resolved);
  } catch (_) { /* ignore */ }
};

const applyAccentDom = (accentKey) => {
  const root = document.documentElement;
  const found = ACCENTS.find((a) => a.key === accentKey) || ACCENTS[0];
  applyAccentPreset(found.key);
  if (!accentKey || accentKey === 'blue') {
    root.setAttribute('data-accent', 'blue');
  } else {
    root.setAttribute('data-accent', accentKey);
  }
  try {
    window.localStorage.setItem('us-accent', found.key);
  } catch (_) { /* ignore */ }
};

const readInitialTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
      || window.localStorage.getItem('us-theme')
      || window.localStorage.getItem('theme_mode');
    return VALID_THEMES.includes(saved) ? saved : 'dark';
  } catch (_) { return 'dark'; }
};

const readInitialAccent = () => {
  if (typeof window === 'undefined') return 'blue';
  try {
    const saved = window.localStorage.getItem(ACCENT_KEY)
      || window.localStorage.getItem('us-accent');
    return VALID_ACCENTS.includes(saved) ? saved : 'blue';
  } catch (_) { return 'blue'; }
};

const isThemeUserPinned = () => {
  try {
    return window.localStorage.getItem(THEME_USER_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const isAccentUserPinned = () => {
  try {
    return window.localStorage.getItem(ACCENT_USER_KEY) === '1';
  } catch (_) {
    return false;
  }
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(readInitialTheme);
  const [accent, setAccentState] = useState(readInitialAccent);
  const publicScopes = useRef(new Set());
  const cachedPublic = useRef(null);
  const publicCurrent = useRef(null);
  const publicChoice = useRef(null);
  const privateColors = useRef(null);
  const personalAccent = useRef(accent);
  personalAccent.current = accent;
  const [publicBranding, setPublicBrandingState] = useState(null);
  const resolved = publicBranding?.brand_theme || preference;

  const enterPublicSurface = useCallback((scope) => {
    if (!publicScopes.current.size) {
      const root = document.documentElement;
      privateColors.current = { accent: root.getAttribute('data-accent'), hex: root.style.getPropertyValue('--primary-brand-color'), selected: personalAccent.current };
    }
    publicScopes.current.add(scope);
    if (!cachedPublic.current) {
      let saved = 'dark';
      try { saved = window.localStorage.getItem(PUBLIC_THEME_STORAGE_KEY) || 'dark'; } catch (_) { /* blocked storage */ }
      cachedPublic.current = { brand_theme: VALID_THEMES.includes(saved) ? saved : 'dark' };
    }
    publicChoice.current = null;
    publicCurrent.current = { ...cachedPublic.current };
    setPublicBrandingState(publicCurrent.current);
    return () => {
      publicScopes.current.delete(scope);
      if (!publicScopes.current.size) {
        const root = document.documentElement, previous = privateColors.current;
        if (previous?.selected !== personalAccent.current || (!previous?.accent && !previous?.hex)) applyAccentDom(personalAccent.current);
        else if (previous) {
          if (previous.accent) root.setAttribute('data-accent', previous.accent);
          else root.removeAttribute('data-accent');
          if (previous.hex) root.style.setProperty('--primary-brand-color', previous.hex);
          else root.style.removeProperty('--primary-brand-color');
        }
        privateColors.current = null;
        publicCurrent.current = null;
        publicChoice.current = null;
        setPublicBrandingState(null);
      }
    };
  }, []);

  const applyPublicBranding = useCallback((data = {}) => {
    if (!publicScopes.current.size) return;
    const next = { ...cachedPublic.current, ...data };
    next.brand_theme = VALID_THEMES.includes(next.brand_theme) ? next.brand_theme : 'dark';
    cachedPublic.current = next;
    try { window.localStorage.setItem(PUBLIC_THEME_STORAGE_KEY, next.brand_theme); } catch (_) { /* blocked storage */ }
    publicCurrent.current = { ...next, brand_theme: publicChoice.current || next.brand_theme };
    setPublicBrandingState(publicCurrent.current);
  }, []);

  useLayoutEffect(() => {
    applyThemeDom(resolved, { persistAliases: !publicBranding });
    if (publicBranding) {
      applyAppearanceColors(publicBranding, resolved, { force: true });
    } else {
      const root = document.documentElement;
      const currentAccent = root.getAttribute('data-accent');
      if (VALID_ACCENTS.includes(currentAccent)) applyAccentPreset(currentAccent, resolved);
      else {
        const customHex = root.style.getPropertyValue('--primary-brand-color');
        if (customHex) applyBrandPrimaryFromHex(customHex, resolved);
      }
    }
  }, [resolved, publicBranding]);
  useEffect(() => { if (!publicScopes.current.size) applyAccentDom(accent); }, [accent]);

  const setPreference = useCallback((pref, opts = {}) => {
    if (!VALID_THEMES.includes(pref)) return;
    if (publicScopes.current.size) {
      publicChoice.current = pref;
      publicCurrent.current = { ...cachedPublic.current, brand_theme: pref };
      setPublicBrandingState(publicCurrent.current);
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, pref);
      // Toggle do utilizador: fixa a escolha para o branding do escritório não reverter
      if (opts.pinUser !== false) {
        window.localStorage.setItem(THEME_USER_KEY, '1');
      }
    } catch (_) { /* ignore */ }
    setPreferenceState(pref);
  }, []);

  const setAccent = useCallback((next) => {
    if (!VALID_ACCENTS.includes(next)) return;
    try {
      window.localStorage.setItem(ACCENT_KEY, next);
      window.localStorage.setItem(ACCENT_USER_KEY, '1');
      window.localStorage.setItem('us-accent', next);
    } catch (_) { /* ignore */ }
    setAccentState(next);
    applyAccentDom(next);
  }, []);

  const syncOfficeBranding = useCallback(({ brand_theme, brand_accent, forceTheme = false, forceAccent = false } = {}) => {
    if (VALID_THEMES.includes(brand_theme)) {
      // Preferência pessoal do header ganha sobre brand_theme do escritório
      if (!forceTheme && isThemeUserPinned()) {
        const keep = readInitialTheme();
        applyThemeDom(keep);
        setPreferenceState(keep);
      } else {
        try {
          window.localStorage.setItem(STORAGE_KEY, brand_theme);
          if (forceTheme) window.localStorage.removeItem(THEME_USER_KEY);
        } catch (_) { /* ignore */ }
        setPreferenceState(brand_theme);
        applyThemeDom(brand_theme);
      }
    }
    if (brand_accent === undefined) return;

    const userPinned = !forceAccent && isAccentUserPinned();

    // Sem accent na dashboard: não forçar azul — a cor primária fica a cargo de applyAppearanceColors
    if (brand_accent === '' || brand_accent === 'neutral') {
      if (userPinned) {
        const keep = readInitialAccent();
        applyAccentDom(keep);
        setAccentState(keep);
      }
      return;
    }

    if (VALID_ACCENTS.includes(brand_accent)) {
      if (userPinned) {
        const keep = readInitialAccent();
        applyAccentDom(keep);
        setAccentState(keep);
        return;
      }
      try {
        window.localStorage.setItem(ACCENT_KEY, brand_accent);
      } catch (_) { /* ignore */ }
      setAccentState(brand_accent);
      applyAccentDom(brand_accent);
    }
  }, []);

  const applyBranding = useCallback(({ brand_theme, brand_accent, forceTheme = false, forceAccent = false } = {}) => {
    if (publicScopes.current.size) {
      applyPublicBranding({ brand_theme, brand_accent });
      return;
    }
    if (VALID_THEMES.includes(brand_theme)) {
      if (!forceTheme && isThemeUserPinned()) {
        const keep = readInitialTheme();
        applyThemeDom(keep);
        setPreferenceState(keep);
      } else {
        try {
          window.localStorage.setItem(STORAGE_KEY, brand_theme);
          if (forceTheme) window.localStorage.removeItem(THEME_USER_KEY);
        } catch (_) { /* ignore */ }
        applyThemeDom(brand_theme);
        setPreferenceState(brand_theme);
      }
    }
    if (brand_accent === undefined) return;

    if (!forceAccent && isAccentUserPinned()) {
      applyAccentDom(readInitialAccent());
      return;
    }

    if (brand_accent === '' || brand_accent === 'neutral') {
      // Páginas públicas: manter --primary do CSS / primary_color (não azul forçado)
      return;
    }

    if (VALID_ACCENTS.includes(brand_accent)) {
      try {
        if (forceAccent) {
          // Páginas públicas: aplica a cor do escritório no DOM/estado desta sessão
          // sem apagar a preferência pessoal do utilizador no dashboard.
          setAccentState(brand_accent);
          applyAccentDom(brand_accent);
          return;
        }
        window.localStorage.setItem(ACCENT_KEY, brand_accent);
      } catch (_) { /* ignore */ }
      setAccentState(brand_accent);
      applyAccentDom(brand_accent);
    }
  }, [applyPublicBranding]);

  const clearLocalAppearanceCache = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(ACCENT_KEY);
      window.localStorage.removeItem(ACCENT_USER_KEY);
      window.localStorage.removeItem(THEME_USER_KEY);
      window.localStorage.removeItem('us-theme');
      window.localStorage.removeItem('us-accent');
    } catch (_) { /* ignore */ }
  }, []);

  const persistUserAppearance = useCallback(async ({ brand_accent, brand_theme } = {}) => {
    const body = {};
    if (brand_accent !== undefined) body.brand_accent = brand_accent;
    if (brand_theme !== undefined) body.brand_theme = brand_theme;
    if (!Object.keys(body).length) return null;
    try {
      const { data } = await axios.put(`${API_URL}/api/account/appearance`, body, { withCredentials: true });
      return data;
    } catch (_) {
      return null;
    }
  }, []);

  const reloadOfficeAppearance = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/account/appearance`, { withCredentials: true });
    syncOfficeBranding({
      brand_theme: data?.brand_theme || undefined,
      brand_accent: data?.brand_accent || undefined,
      forceTheme: true,
      forceAccent: true,
    });
    return data;
  }, [syncOfficeBranding]);

  const cycleTheme = useCallback(() => {
    setPreference(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setPreference]);

  return (
    <ThemeContext.Provider
      value={{
        preference: resolved,
        resolved,
        theme: resolved,
        publicBranding,
        enterPublicSurface,
        applyPublicBranding,
        setPreference,
        setTheme: setPreference,
        cycleTheme,
        toggleTheme: cycleTheme,
        accent,
        setAccent,
        accentPresets: ACCENT_PRESETS,
        accents: ACCENTS,
        applyBranding,
        syncOfficeBranding,
        clearLocalAppearanceCache,
        reloadOfficeAppearance,
        persistUserAppearance,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      preference: 'light',
      resolved: 'light',
      theme: 'light',
      setPreference: () => {},
      setTheme: () => {},
      cycleTheme: () => {},
      toggleTheme: () => {},
      accent: 'blue',
      setAccent: () => {},
      accentPresets: ACCENT_PRESETS,
      accents: ACCENTS,
      applyBranding: () => {},
      syncOfficeBranding: () => {},
      clearLocalAppearanceCache: () => {},
      reloadOfficeAppearance: async () => ({}),
      persistUserAppearance: async () => null,
    };
  }
  return ctx;
};

export default ThemeContext;
